import { MultiSelectCreatable, MultiSelectOption } from "@/components/misc/Multiselect";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { ClassroomForm } from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Instrument } from "@/javascript/music-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, NumberInput, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

interface AdminClassroomCreatorProps {
	onClose: () => void;
	onCreated: () => void;
}

const AdminClassroomCreator: React.FC<AdminClassroomCreatorProps> = ({ onClose, onCreated }) => {
	const [instruments, setInstruments] = useState<Instrument[]>([]);
	const [loadingInstruments, setLoadingInstruments] = useState<boolean>(true);
	const [songs, setSongs] = useState<Song[]>([]);
	const [loadingSongs, setLoadingSongs] = useState<boolean>(true);

	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminClassroomCreator must be a child of AuthContext.");
	}
	const token = auth.token;
	if (token === null) {
		return null;
	}

	// Set up the form with initial values and validations.
	const form = useForm({
		initialValues: {
			name: "",
			studentLimit: 0,
			classroomAdminLimit: 0,
			defaultInstruments: [] as MultiSelectOption[],
			defaultSongs: [] as MultiSelectOption[],
		},
		validate: {
			name: (value) => (value.trim().length === 0 ? "Classroom name is required" : null),
			studentLimit: (value) =>
				value < 0 ? "Student limit must not be negative" : null,
			classroomAdminLimit: (value) =>
				value < 0 ? "Classroom admin must not be negative" : null,
			// You could add additional validation for defaultInstruments if needed.
		},
	});

	// Fetch instrument options once when the component mounts.
	useEffect(() => {
		const fetchInstruments = async () => {
			try {
				const instruments = await ConductionAPI.getInstruments();
				setInstruments(instruments || []);
			} catch (error) {
				console.error("Error fetching instrument options", error);
			} finally {
				setLoadingInstruments(false);
			}
		};

		const fetchSongs = async () => {
			try {
				const songs = await ConductionAPI.getAllSongs(token);
				setSongs(songs || []);
			} catch (error) {
				console.error("Error fetching instrument options", error);
			} finally {
				setLoadingSongs(false);
			}
		};

		fetchInstruments();
		fetchSongs();
	}, []);

	const handleSubmit = async () => {
		// Validate the form and display errors if necessary.
		if (!form.isValid()) {
			form.validate();
			return;
		}

		const notificationId = Utils.displayNotification(
			"Creating Classroom...",
			"Your new classroom is being created.",
			"blue",
			"loading"
		);

		const classroomForm: ClassroomForm = {
			name: form.values.name,
			studentLimit: form.values.studentLimit,
			classroomAdminLimit: form.values.classroomAdminLimit,
			defaultInstrumentIds: form.values.defaultInstruments.map((instr) =>
				instr.value.toString()
			),
			defaultSongIds: form.values.defaultSongs.map((song) =>
				song.value.toString()
			),
		};

		try {
			await ConductionAPI.createClassroom(classroomForm, token);
			Utils.updateNotification(
				notificationId,
				"Classroom Created",
				undefined,
				"green",
				<IconCheck size={16} />
			);
			onCreated();
			onClose();
		} catch (error) {
			console.error("Error creating classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to create classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const instrumentOptions: MultiSelectOption[] = instruments.map((instrument) => ({
		label: instrument.name,
		value: instrument.id,
	}));

	const songOptions: MultiSelectOption[] = songs.map((song) => ({
		label: `${song.songName} by ${song.artists.map(artist => artist.artistName).join(", ")}`,
		value: song.songId
	}))

	const height = 40; // Use 40vh for view height as in the AdminSongCreator

	return (
		<ConductionModal
			onClose={onClose}
			title="Create New Classroom"
			size="lg"
		>
			<Box style={{ minHeight: `${height}vh` }}>
				{loadingInstruments || loadingSongs ? (
					<Skeleton height={"100%"} radius="md" />
				) : (
					<form onSubmit={form.onSubmit(handleSubmit)}>
						<Flex
							direction="column"
							justify="space-between"
							align="stretch"
							style={{ minHeight: `${height}vh` }}
							onKeyDown={(e) => e.stopPropagation()} // Prevent hotkey conflicts
							onKeyUp={(e) => e.stopPropagation()}
						>
							<Stack>
								<TextInput
									label="Classroom Name"
									{...form.getInputProps("name")}
									required
								/>

								<NumberInput
									label="Student Limit"
									{...form.getInputProps("studentLimit")}
									required
								/>

								<NumberInput
									label="Classroom Admin Limit"
									{...form.getInputProps("classroomAdminLimit")}
									required
								/>

								<Stack>
									<Text fw={500}>Default Instruments</Text>
									<MultiSelectCreatable
										options={instrumentOptions}
										value={form.values.defaultInstruments}
										creatable={false}
										{...form.getInputProps("defaultInstruments")}
									/>
								</Stack>

								<Stack>
									<Text fw={500}>Default Songs</Text>
									<MultiSelectCreatable
										options={songOptions}
										value={form.values.defaultSongs}
										creatable={false}
										{...form.getInputProps("defaultSongs")}
									/>
								</Stack>
							</Stack>

							<Button
								type="submit"
								radius="xl"
								size="sm"
								loading={form.submitting}
								color="red"
							>
								Create Classroom
							</Button>
						</Flex>
					</form>
				)}
			</Box>
		</ConductionModal>
	);
};

export default AdminClassroomCreator;
